import { useEffect, useState, useRef } from "react";
import { isVitalsSubdomain } from "../util/subdomain";

// Extend Window interface to include custom property for hash tracking
declare global {
  interface Window {
    __lastHash?: string;
  }
}

// Constants for timing and intervals
export const REDIRECT_COUNTDOWN_SECONDS = 4;
const HASH_CHECK_INTERVAL_MS = 200; // Check every 200ms (5 times per second)
const STATUS_CHECK_INTERVAL_MS = 2000; // Check status every 2 seconds
const DOM_UPDATE_DELAY_MS = 100;

/**
 * Hook to detect if the app is running on vitals.circadia.link subdomain
 * or localhost/172.20.10.2 (for testing) and redirect to the vitals-ai route immediately on page load.
 * Exceptions:
 * - Allows access to login pages - users must login first
 * - Allows access to change-facility page - users can switch facilities
 * - Allows access to patient profile pages for vitals history viewing
 * - For status=3 (resident did not consent): shows consent message for 4 seconds then redirects to vitals-ai
 * - Allows access to patient profiles with other consent status for back navigation
 */
export const useVitalsSubdomainRedirect = () => {
  const [showCountdown, setShowCountdown] = useState(false);

  // Use ref to track latest showCountdown value to avoid stale closure in intervals
  const showCountdownRef = useRef(showCountdown);
  useEffect(() => {
    showCountdownRef.current = showCountdown;
  }, [showCountdown]);

  const handleRedirect = () => {
    const redirectUrl = `${window.location.origin}/#/vitals-ai`;
    window.location.href = redirectUrl;
  };

  const checkAndRedirect = () => {
    // Check if we're on the vitals subdomain
    if (isVitalsSubdomain()) {
      // Get current path and search params
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const currentHash = window.location.hash;

      // Check if we're already on the vitals-ai route or patient profile route
      const isAlreadyOnVitalsAI =
        currentHash === "#/vitals-ai" || currentPath.includes("vitals-ai");
      const isOnPatientProfile =
        currentHash.includes("#/patient-profile") ||
        currentPath.includes("patient-profile");

      // Check if we're on login/auth pages - don't redirect if user needs to login first
      const isOnLoginPage =
        currentHash.includes("#/login") || currentPath.includes("login");

      // Without this check, someone signing in via PCC can get stuck in a loop that makes them login twice. (Tested by Umer.)
      const isSSOLoginPage =
        currentHash.includes("#/sso/login") ||
        currentPath.includes("sso/login");

      const isOnUserInfoPage =
        currentHash.includes("#/user-info") ||
        currentPath.includes("user-info");

      // Check if we're on change-facility page - allow users to change facility
      const isOnChangeFacility =
        currentHash.includes("#/change-facility") ||
        currentPath.includes("change-facility");

      // Check for consent-related query parameters that might indicate frozen screen
      const hasConsentStatus =
        currentSearch.includes("status=") ||
        currentSearch.includes("consent") ||
        currentSearch.includes("denied") ||
        currentSearch.includes("archived");

      // Specifically check for status=3 which indicates resident did not consent
      // Check both search params and hash for status=3
      const hasStatus3 =
        currentSearch.includes("status=3") || currentHash.includes("status=3");

      // Don't redirect if user is on login page - let them login first
      if (isOnLoginPage || isSSOLoginPage || isOnUserInfoPage) {
        setShowCountdown(false);
        return;
      }

      // Don't redirect if user is on change-facility page - let them change facility
      if (isOnChangeFacility) {
        setShowCountdown(false);
        return;
      }

      // Handle status=3 case: show countdown UI for 4 seconds then redirect
      if (hasStatus3 && isOnPatientProfile) {
        setShowCountdown(true);
        // Don't redirect immediately, let the user see the countdown
        return;
      } else {
        // Hide countdown if not on status=3 page
        setShowCountdown(false);
      }

      if (
        !isAlreadyOnVitalsAI &&
        !isOnPatientProfile &&
        !isOnLoginPage &&
        !isSSOLoginPage &&
        !isOnChangeFacility &&
        !hasConsentStatus &&
        !isOnUserInfoPage
      ) {
        // Redirect to vitals-ai route with preserved search params
        const redirectUrl = `${window.location.origin}/#/vitals-ai${currentSearch}`;

        try {
          window.location.href = redirectUrl;
        } catch (error) {
          console.error("Failed to redirect to vitals-ai:", error);
          // Fallback: try to navigate using history API
          try {
            window.history.replaceState(null, "", redirectUrl);
            window.location.reload();
          } catch (fallbackError) {
            console.error("Fallback redirect also failed:", fallbackError);
          }
        }
      }
    }
  };

  useEffect(() => {
    // Check on initial mount
    checkAndRedirect();

    // Also listen for hash changes (for SPA navigation)
    const handleHashChange = () => {
      checkAndRedirect();
    };

    // Listen for popstate events (back/forward navigation)
    const handlePopState = () => {
      checkAndRedirect();
    };

    // Set up a MutationObserver to detect DOM changes that might indicate page content changes
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain patient profile content
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.textContent?.includes("Resident did not consent") ||
                element.querySelector?.('[class*="consent"]') ||
                element.querySelector?.('[class*="denied"]')
              ) {
                shouldCheck = true;
              }
            }
          });
        }
      });

      if (shouldCheck) {
        // Add a small delay to ensure DOM is fully updated
        setTimeout(checkAndRedirect, DOM_UPDATE_DELAY_MS);
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);

    // Only set up polling intervals if on vitals subdomain (performance optimization)
    let intervalId: NodeJS.Timeout | undefined;
    let statusCheckInterval: NodeJS.Timeout | undefined;

    if (isVitalsSubdomain()) {
      // Set up an interval to check for hash changes (fallback for missed events)
      intervalId = setInterval(() => {
        const currentHash = window.location.hash;
        if (currentHash !== window.__lastHash) {
          window.__lastHash = currentHash;
          checkAndRedirect();
        }
      }, HASH_CHECK_INTERVAL_MS);

      // Additional periodic check for status=3 detection
      statusCheckInterval = setInterval(() => {
        const currentHash = window.location.hash;
        const currentSearch = window.location.search;
        const hasStatus3 =
          currentSearch.includes("status=3") ||
          currentHash.includes("status=3");
        const isOnPatientProfile =
          currentHash.includes("#/patient-profile") ||
          window.location.pathname.includes("patient-profile");

        // Use ref to get latest showCountdown value (avoid stale closure)
        if (hasStatus3 && isOnPatientProfile && !showCountdownRef.current) {
          checkAndRedirect();
        }
      }, STATUS_CHECK_INTERVAL_MS);

      // Initialize the last hash
      window.__lastHash = window.location.hash;
    }

    // Cleanup
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
      if (statusCheckInterval) clearInterval(statusCheckInterval);
    };
  }, []); // Run only once on mount

  return {
    isVitalsSubdomain: isVitalsSubdomain(),
    showCountdown,
    handleRedirect,
  };
};
